import { message } from 'antd';
import { COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS } from '../actions/compliance_requests_actions';
import { fetchComplianceSummary } from './compliance_request_instances_thunks';
import { fetchComplianceSupplierStatuses } from './compliance_statuses_thunks';
import RestCrudThunks from './restCrudThunksGenerator';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { ROLE_TYPES } from '../utils/DataConstants';

const fetchUtils = new FetchUtils();

// Proposed backend contract: /api/v1/buyer/compliance/requests
// Compliance requests are authored by buyers only; suppliers see per-supplier
// request instances instead (compliance_request_instances_thunks).
const complianceRequestsRestCrudThunksForBuyer = new RestCrudThunks(
	'requests',
	'compliance_request',
	'complianceRequest',
	'complianceRequests',
	COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	true,
	'compliance/'
);

// Internal-tech suppliers act as the buyer and author compliance requests via the
// supplier endpoints (/supplier/compliance/requests -> SupplierComplianceRequestController).
const complianceRequestsRestCrudThunksForSupplier = new RestCrudThunks(
	'requests',
	'compliance_request',
	'complianceRequest',
	'complianceRequests',
	COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	true,
	'compliance/'
);

export const complianceRequestsRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? complianceRequestsRestCrudThunksForSupplier
		: complianceRequestsRestCrudThunksForBuyer;

// GET /api/v1/{role}/compliance/requests/with_instance_counts
// Hydrated requests-tab read: each request comes back with `instanceStatusCounts`
// and `targetedSupplierCount` (the plain /requests list has no counts). Dispatches
// the same fetchStart/fetchSuccess pair the generated CRUD read uses, so it
// populates the compliance_requests slice + target collection with pagination and
// total count. Signature mirrors RestCrudThunks.read so PaginatedReduxTable can
// call it directly as its fetchData prop.
export const fetchComplianceRequestsWithInstanceCounts =
	(userType) =>
	(
		params = {},
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) => {
		const baseUrl = getBackendUri();
		const apiUrl = `/api/v1/${userType}/compliance/requests/with_instance_counts`;
		let queryParams = { ...params };
		if (pagination) {
			queryParams = {
				...queryParams,
				offset: (pagination.current - 1) * pagination.pageSize,
				limit: pagination.pageSize,
			};
		}
		if (sorting) {
			queryParams = {
				...queryParams,
				...sorting,
				order: sorting.order === 'ascend' ? 'asc' : 'desc',
			};
		}
		if (filters) {
			queryParams = { ...queryParams, ...filters };
		}
		return (dispatch) => {
			return new Promise((resolve, reject) => {
				dispatch(COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));
				fetchUtils
					.get(
						baseUrl + apiUrl,
						Object.assign(
							{ headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
							queryParams
						)
					)
					.then(parseJSON)
					.then(({ json, status }) => {
						const d = json;
						if (status === 401) {
							dispatch(
								COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.fetchError(
									'unauthorized',
									targetCollectionName
								)
							);
							dispatch(logoutSuccess());
							reject('unauthorized');
							return;
						}
						if (d.status === 'error') {
							dispatch(
								COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.fetchError(d.message, targetCollectionName)
							);
							reject(d.message);
						} else {
							dispatch(
								COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.fetchSuccess(
									d.data,
									d.count,
									targetCollectionName,
									{ ...pagination, total: d.count },
									sorting,
									filters,
									addToTargetCollection
								)
							);
							resolve(d.data);
						}
					})
					.catch((d) => {
						dispatch(
							COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.fetchError(d.message, targetCollectionName)
						);
						reject(d.message);
					});
			});
		};
	};

// POST /api/v1/{role}/compliance/requests/:id/publish
// Publishing a draft fans out one request instance per targeted supplier.
export const publishComplianceRequest = (userType) => (complianceRequestId) => {
	const baseUrl = getBackendUri();
	const apiUrl = `/api/v1/${userType}/compliance/requests/${complianceRequestId}/publish`;
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.updateStart({ id: complianceRequestId }));
			fetchUtils
				.post(baseUrl + apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
						reject('unauthorized');
						return;
					}
					if (d.status === 'error') {
						dispatch(
							COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.updateError(d.message, {
								id: complianceRequestId,
							})
						);
						message.error('Failed to publish compliance request.');
						reject(d.message);
					} else {
						dispatch(COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.updateSuccess(d.data));
						// Publishing fans out instances that re-derive party compliance.
						// Proactively refetch the shared derived state (summary banner +
						// supplier party statuses) into redux so consumers re-render — no
						// cross-component refresh signal needed.
						dispatch(fetchComplianceSummary(userType)());
						dispatch(fetchComplianceSupplierStatuses(userType)());
						message.success('Compliance request published.');
						resolve(d.data);
					}
				})
				.catch((d) => {
					dispatch(
						COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.updateError(d.message, {
							id: complianceRequestId,
						})
					);
					message.error('Failed to publish compliance request.');
					reject(d.message);
				});
		});
	};
};
